// src/components/ThinkingSteps.tsx
// Progressive thinking/reasoning steps display component
// Shows AI's thought process in expandable steps similar to Manus AI

import React, { useState } from 'react';

export interface ThinkingStep {
  id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  details?: string;
  timestamp?: Date;
  progress?: number; // 0-100
  isExpandable?: boolean;
}

interface Props {
  steps: ThinkingStep[];
  className?: string;
}

interface TransformedMessage {
  text: string;
  icon: React.ReactNode;
  actionType: string;
}

const ThinkingSteps: React.FC<Props> = ({ steps, className = '' }) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  // Transform backend status messages to Manus-style format
  const transformMessage = (message: string): TransformedMessage => {
    // Searching patterns
    if (message.match(/searching the web for:?/i)) {
      const query = message.replace(/searching the web for:?\s*/i, '').replace(/\.\.\.$/, '');
      return {
        text: `Searching ${query}`,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        ),
        actionType: 'search'
      };
    }

    // Knowledge base searching
    if (message.match(/searching knowledge base/i)) {
      return {
        text: 'Searching knowledge base',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        ),
        actionType: 'search'
      };
    }

    // Found results
    if (message.match(/found \d+ (sources|documents|results)/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        actionType: 'success'
      };
    }

    // Code execution
    if (message.match(/executing .+ code/i)) {
      const language = message.match(/executing (.+?) code/i)?.[1] || 'code';
      return {
        text: `Executing ${language} code`,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        ),
        actionType: 'execute'
      };
    }

    // Code executed successfully
    if (message.match(/code executed in/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ),
        actionType: 'success'
      };
    }

    // Generated files
    if (message.match(/generated \d+ file/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        actionType: 'success'
      };
    }

    // Browsing (can be added when backend sends these messages)
    if (message.match(/browsing/i)) {
      const url = message.replace(/browsing\s*/i, '').replace(/\.\.\.$/, '');
      return {
        text: `Browsing ${url}`,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        ),
        actionType: 'browse'
      };
    }

    // Scraping patterns
    if (message.match(/scraping/i)) {
      // Clean up the message
      let scrapingText = message.replace(/\.\.\.$/, '');
      // Extract domain if it's a URL
      const urlMatch = scrapingText.match(/https?:\/\/([^\/\s]+)/);
      if (urlMatch) {
        scrapingText = `Scraping ${urlMatch[1]}`;
      }
      return {
        text: scrapingText,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        ),
        actionType: 'scrape'
      };
    }

    // Planning patterns
    if (message.match(/planning .+ structure/i) || message.match(/preparing/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        ),
        actionType: 'plan'
      };
    }

    // Generating patterns
    if (message.match(/generating/i) || message.match(/stitching/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        actionType: 'generate'
      };
    }

    // Analyzing patterns
    if (message.match(/analyzing/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        actionType: 'analyze'
      };
    }

    // Bypassing patterns
    if (message.match(/bypassing/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        ),
        actionType: 'bypass'
      };
    }

    // No results patterns
    if (message.match(/no (results|documents) found/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        ),
        actionType: 'empty'
      };
    }

    // Presentation/Success ready patterns
    if (message.match(/ready|completed successfully/i)) {
      return {
        text: message,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        actionType: 'success'
      };
    }

    // Default - return as-is
    return {
      text: message,
      icon: null,
      actionType: 'default'
    };
  };

  const getStatusIcon = (status: ThinkingStep['status'], step: ThinkingStep) => {
    // For in_progress or pending, use the action-specific icon
    if ((status === 'in_progress' || status === 'pending') && step.title) {
      const transformed = transformMessage(step.title);
      if (transformed.icon) {
        return <span className="text-blue-500">{transformed.icon}</span>;
      }
    }

    // Status-based icons
    switch (status) {
      case 'completed':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'in_progress':
        return (
          <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'pending':
        return (
          <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth={2} />
          </svg>
        );
    }
  };

  const getStatusColor = (status: ThinkingStep['status']) => {
    switch (status) {
      case 'completed':
        return 'border-green-500/30 bg-green-500/5';
      case 'in_progress':
        return 'border-blue-500/30 bg-blue-500/5';
      case 'failed':
        return 'border-red-500/30 bg-red-500/5';
      case 'pending':
        return 'border-border/30 bg-muted/5';
    }
  };

  if (steps.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      {steps.map((step, index) => {
        const isExpanded = expandedSteps.has(step.id);
        const hasDetails = step.details && step.details.trim().length > 0;

        return (
          <div
            key={step.id}
            className={`rounded-lg border ${getStatusColor(step.status)} transition-all duration-300 animate-fade-in-up overflow-hidden`}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div
              className={`flex items-center justify-between p-3 ${
                hasDetails && step.isExpandable !== false ? 'cursor-pointer hover:bg-muted/10' : ''
              }`}
              onClick={() => hasDetails && step.isExpandable !== false && toggleStep(step.id)}
            >
              <div className="flex items-center space-x-3 flex-1">
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {getStatusIcon(step.status, step)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {transformMessage(step.title).text}
                  </p>
                  {step.progress !== undefined && step.status === 'in_progress' && (
                    <div className="mt-1.5 w-full bg-muted/30 rounded-full h-1">
                      <div
                        className="bg-blue-500 h-1 rounded-full transition-all duration-500"
                        style={{ width: `${step.progress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Timestamp hidden — not useful for users */}
                {hasDetails && step.isExpandable !== false && (
                  <span className={`text-muted-foreground transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                )}
              </div>
            </div>

            {/* Expandable details */}
            {hasDetails && isExpanded && (
              <div className="px-3 pb-3 pt-0 animate-fade-in">
                <div className="pl-9 pr-2">
                  <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2 border border-border/30 font-mono whitespace-pre-wrap">
                    {step.details}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ThinkingSteps;
